import Layout from "@/containers/Layout";
import Header from "./partials/header";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getTagMentionsV4 } from "@/api/endpoints/searches";
import MentionCardNew from "@components/MentionCardNew";
import { MentionCardsProvider } from "@/containers/MentionCardsContext";
import { getTags } from "@/api/endpoints/tags";
import PaginationNew from "@components/PaginationNew";
import { useEffect, useState } from "react";
import { Skeleton } from "@components/ui/skeleton";
import ErrorComponent from "@components/ui/error";
import { Trans, useLingui } from "@lingui/react/macro";
import { AnimatePresence, motion } from "motion/react";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@components/ui/empty";
import { MessagesSquareIcon } from "lucide-react";
import { useFilterParamsV4 } from "@/hooks/useFilterParams";
import TagShareDialog from "./partials/TagShareDialog";
import { postTrackingData } from "@/api/endpoints/track";
import { EVENT } from "@/tracking_events";
import TagExportDialog from "./partials/TagExportDialog";

export default function TagView() {
  const { t } = useLingui();

  // URL state
  const { slug = "favorited", orgUuid } = useParams<{
    orgUuid: Uuid;
    slug: string;
  }>();

  const isFavoritePage = slug === "favorited";

  const [filters] = useFilterParamsV4();

  // Local state
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isSharing, setIsSharing] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);

  // Server state
  const { data: tagsData } = useQuery({
    queryKey: [orgUuid, "tags"],
    queryFn: () => getTags(),
    refetchOnWindowFocus: false,
  });

  const tags = tagsData?.ok
    ? tagsData.data.filter((tag) => tag.slug !== "favorited")
    : [];

  const tag = tagsData?.ok
    ? tagsData.data.find((tag) => tag.slug === slug)
    : undefined;

  const {
    data: mentionCardsData,
    isLoading: isMentionCardsLoading,
    error: mentionCardsError,
  } = useQuery({
    queryKey: [orgUuid, "tags", slug, "mentions", filters, page],
    queryFn: async () => {
      const res = await getTagMentionsV4(slug, { ...filters, page }, orgUuid);

      if (!res.ok) {
        throw new Error();
      }

      return res;
    },
  });

  const mentionCards = mentionCardsData?.ok
    ? mentionCardsData.data.results
    : undefined;

  const pageCount = mentionCardsData?.ok ? mentionCardsData.data.page_count : 0;

  // Side-effects
  useEffect(() => {
    setPage(1);
  }, [filters, slug]);

  return (
    <Layout>
      <Header
        tagTitle={tag?.name}
        isFavoritePage={isFavoritePage}
        onShare={() => {
          postTrackingData(EVENT.SHARE_TAG);
          setIsSharing(true);
        }}
        onExport={() => {
          postTrackingData(EVENT.EXPORT_TAG);
          setIsExporting(true);
        }}
      />

      <div className="max-w-7xl p-6 flex flex-col gap-8">
        <AnimatePresence mode="popLayout">
          {mentionCards ? (
            <MentionCardsProvider>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col gap-5"
                key="mentions"
              >
                {mentionCards.length ? (
                  <>
                    {mentionCards.map((mentionCard) => {
                      return (
                        <MentionCardNew
                          mentionCard={mentionCard}
                          key={mentionCard.episode.id}
                          orgUuid={orgUuid}
                          tags={tags}
                        />
                      );
                    })}

                    <PaginationNew
                      page={page}
                      max={pageCount}
                      onChangePage={setPage}
                    />
                  </>
                ) : (
                  <Empty>
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <MessagesSquareIcon />
                      </EmptyMedia>
                      <EmptyTitle>
                        <Trans>Inga omnämnanden</Trans>
                      </EmptyTitle>
                    </EmptyHeader>
                  </Empty>
                )}
              </motion.div>
            </MentionCardsProvider>
          ) : isMentionCardsLoading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col gap-5"
              key="skeletons"
            >
              <Skeleton className="w-full h-64" />
              <Skeleton className="w-full h-64" />
              <Skeleton className="w-full h-64" />
            </motion.div>
          ) : mentionCardsError ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              key="error"
            >
              <ErrorComponent
                heading={t`Åh nej!`}
                description={t`Något gick snett med att ladda omnämnanden. Vänligen försök igen senare`}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>

      <TagShareDialog
        isFavoritePage={isFavoritePage}
        onOpenChange={setIsSharing}
        orgUuid={orgUuid}
        open={isSharing}
        tag={tag}
      />

      <TagExportDialog
        isFavoritePage={isFavoritePage}
        onOpenChange={setIsExporting}
        open={isExporting}
        filters={filters}
        tag={tag}
      />
    </Layout>
  );
}
